import Component from '@glimmer/component';
import { Section, VStack } from 'tio-ui/components/layout';
import { PhArrowUpRight, PhTrash } from 'ember-phosphor-icons';
import { htmlSafe } from '@ember/template';
import { eq } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import { LinkTo } from '@ember/routing';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import type LpnPage from '../../models/lpn-page';
import type CompanySettingModel from '../../models/company-setting';
import type FeaturesService from '../../services/features';
import type RouterService from '@ember/routing/router-service';
import type StoreService from '@ember-data/store';

interface LpnListCardSignature {
  Args: {
    lpn: LpnPage;
    companySettings?: CompanySettingModel;
  };
  Element: HTMLElement;
  Blocks: Record<string, never>;
}

interface LpnDescriptionSignature {
  Args: {
    lpn: LpnPage;
    companySettings?: CompanySettingModel;
  };
  Element: HTMLElement;
  Blocks: Record<string, never>;
}

class LpnDescription extends Component<LpnDescriptionSignature> {
  get firstParagraph() {
    // Company override description takes priority, then fall back to global LPN description
    const companyBlocks = this.args.lpn.setting?.description?.blocks ?? [];
    const lpnBlocks = this.args.lpn.description?.blocks ?? [];
    const blocks = companyBlocks.length > 0 ? companyBlocks : lpnBlocks;

    const paragraph = blocks.find((b) => b.type === 'paragraph');
    return htmlSafe(paragraph?.data?.text ?? blocks[0]?.data?.text ?? '');
  }

  <template>
    <p class="text-gray-600 line-clamp-3">
      {{this.firstParagraph}}
    </p>
  </template>
}

export default class LpnListCard extends Component<LpnListCardSignature> {
  @tracked isDeleteModalOpen = false;
  @tracked isDeleting = false;
  @service declare features: FeaturesService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  get isCompanyCreated() {
    return this.args.lpn.isCompanyCreated;
  }

  get companyLpnSettings() {
    return this.args.lpn.setting;
  }

  get hasDiscount() {
    if (this.args.lpn.hasDiscount && this.args.lpn.discountPercent) {
      return true;
    }
    return this.companyLpnSettings && this.companyLpnSettings.hasDiscount === true;
  }

  get calculatedDiscount() {
    if (this.companyLpnSettings && this.companyLpnSettings.hasDiscount === true) {
      return this.companyLpnSettings.discountPercent;
    }
    return this.args.lpn.discountPercent;
  }

  get isEmployerRecommended() {
    if (this.isCompanyCreated) {
      return true;
    }
    return this.companyLpnSettings?.employerRecommended || false;
  }

  get hasCappedTuition() {
    if (this.isCompanyCreated) {
      return this.args.lpn.badgeCappedTuition;
    }
    return this.companyLpnSettings?.cappedTuition || false;
  }

  get badges() {
    const list = [];

    if (this.isEmployerRecommended) {
      list.push({
        class: '!bg-[#00809d] !text-white',
        text: 'Employer Recommended',
      });
    }

    if (this.hasCappedTuition) {
      list.push({
        class: '!bg-violet-600 !text-white dark:!bg-violet-500 dark:!text-white',
        text: 'No Out-of-Pocket $',
      });
    }
    if (this.hasDiscount) {
      if (list.length < 2) {
        list.push({
          class: '!bg-violet-100 !text-violet-800 dark:!bg-violet-800 dark:!text-violet-100',
          text: `Save ${this.calculatedDiscount}%`,
        });
      }
    }

    if (this.args.lpn.badgeFamilySavings) {
      if (list.length < 2) {
        list.push({
          class: '!bg-violet-50 !text-violet-600 dark:!bg-violet-800/50 dark:!text-violet-300',
          text: 'Family Savings',
        });
      }
    }

    if (this.args.lpn.badgeFeesWaived) {
      if (list.length < 2) {
        list.push({
          class:
            '!bg-white !text-violet-600 !ring-1 !ring-violet-300 dark:!bg-gray-800 dark:!text-violet-300 dark:!ring-violet-600',
          text: 'Fee Waiver',
        });
      }
    }

    return list;
  }

  openDeleteModal = () => {
    this.isDeleteModalOpen = true;
  };

  closeDeleteModal = () => {
    this.isDeleteModalOpen = false;
  };

  deletePartner = async () => {
    const parentPage = this.args.lpn;

    try {
      this.isDeleting = true;
      parentPage.deleteRecord();
      await parentPage.save();

      this.router.transitionTo('authenticated.lpn.index');
      this.store.unloadRecord(this.args.lpn);
    } catch (error) {
      this.isDeleting = false;
      console.error('Error deleting partner:', error);
    }
  };

  <template>
    <VStack>
      <Section>
        <div class="flex flex-col p-4 min-h-[180px]">
          <div class="flex items-center justify-between mb-2">
            {{#if this.features.isAdminApp}}
              <LinkTo @route="authenticated.lpn.edit" @model={{@lpn.id}}>
                <h1
                  class="text-lg font-semibold text-ocean-800 underline line-clamp-1"
                >{{@lpn.schoolName}}</h1>
              </LinkTo>
              <div class="flex items-center gap-4">
                <PhTrash
                  class="hover:cursor-pointer text-red-600 w-6 h-6"
                  {{on "click" this.openDeleteModal}}
                />
                <LinkTo @route="authenticated.lpn.edit" @model={{@lpn.id}}>
                  <PhArrowUpRight class="text-primary-800 w-6 h-6" />
                </LinkTo>
              </div>
            {{else}}
              <a href={{@lpn.url}} target="_blank" rel="noopener noreferrer">
                <h1
                  class="text-lg font-semibold text-ocean-800 underline line-clamp-1"
                >{{@lpn.schoolName}}</h1>
              </a>
              <a href={{@lpn.url}} target="_blank" rel="noopener noreferrer">
                <PhArrowUpRight class="text-primary-500 w-6 h-6" />
              </a>
            {{/if}}
          </div>
          <div class="flex items-center gap-1 overflow-hidden mb-4">
            {{#each this.badges as |badge index|}}
              <Chip
                class="{{badge.class}}
                  {{if (eq index 1) 'truncate max-w-[calc(100%-theme(spacing.20))]'}}"
              >
                <span class="text-xs whitespace-nowrap">{{badge.text}}</span>
              </Chip>
            {{/each}}
          </div>
          <LpnDescription @lpn={{@lpn}} @companySettings={{@companySettings}} />
        </div>
      </Section>
    </VStack>
    <Modal
      @isOpen={{this.isDeleteModalOpen}}
      @onClose={{this.closeDeleteModal}}
      @ariaLabel={{t "common.aria_labels.delete_partner_confirmation"}}
    >
      <:header>{{t "lpn.delete_partner_confirmation"}}</:header>
      <:body>
        {{t "invoices.permanent_action"}}
      </:body>
      <:footer>
        <Button @appearance="minimal" class="mr-4" {{on "click" this.closeDeleteModal}}>
          {{t "common.cancel"}}
        </Button>
        <Button @intent="danger" {{on "click" this.deletePartner}} @isRunning={{this.isDeleting}}>
          {{t "common.delete"}}
        </Button>
      </:footer>
    </Modal>
  </template>
}
